import React, { useState } from 'react';
import { Alert, FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../context/AuthContext';
import DepositService from '../services/depositService';
import { Deposit } from '../types/Deposit';
import { ErrorLogger } from '../utils/errorLogger';
import { formatAmount } from '../utils/formatAmount';
import { formatRelativeDate } from '../utils/formatDate';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';

interface DepositListProps {
  onDeleteSuccess?: () => void;
}

const DepositList: React.FC<DepositListProps> = ({ onDeleteSuccess }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { data: deposits, loading } = useFirestoreSubscription(
    (onUpdate, onError) => {
      if (!user?.uid) { onUpdate([]); return () => {}; }
      return DepositService.subscribeToAllDeposits(onUpdate, onError);
    },
    [] as Deposit[],
  );

  const [depositToDelete, setDepositToDelete] = useState<Deposit | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!depositToDelete?.id) return;
    try {
      setDeleting(true);
      await DepositService.deleteDeposit(depositToDelete.id);
      onDeleteSuccess?.();
    } catch (error) {
      ErrorLogger.logError(error as any, 'DepositList.handleDeleteConfirm');
      Alert.alert('Error', 'Failed to delete deposit. Please try again.');
    } finally {
      setDeleting(false);
      setDepositToDelete(null);
    }
  };

  const getStatusColors = (status: string) => {
    if (status === 'completed') {
      return { bg: theme.colors.secondaryContainer, text: theme.colors.onSecondaryContainer };
    }
    return { bg: theme.colors.surfaceVariant, text: theme.colors.onSurfaceVariant };
  };

  const renderItem = ({ item }: { item: Deposit }) => {
    const { bg: statusBg, text: statusText } = getStatusColors(item.status);
    return (
      <View style={[styles.row, { backgroundColor: theme.colors.surface, borderBottomColor: theme.colors.outline }]}>
        <View style={[styles.rowAccent, { backgroundColor: theme.colors.primary }]} />
        <View style={styles.rowContent}>
          <Text style={[styles.rowTitle, { color: theme.colors.onSurface }]} numberOfLines={1}>
            {item.description || 'Deposit'}
          </Text>
          <Text style={[styles.rowSubtitle, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
            {formatRelativeDate(item.depositDate)}
            {item.truck ? ` · ${item.truck.plateNumber}` : ''}
          </Text>
        </View>
        <View style={styles.rowRight}>
          <Text style={[styles.rowAmount, { color: theme.colors.primary }]}>
            {formatAmount(item.amount)}
          </Text>
          <View style={[styles.statusBadge, { backgroundColor: statusBg }]}>
            <Text style={[styles.statusText, { color: statusText }]}>{item.status}</Text>
          </View>
        </View>
        <TouchableOpacity style={styles.deleteBtn} onPress={() => setDepositToDelete(item)}>
          <MaterialCommunityIcons name="delete-outline" size={18} color={theme.colors.onSurfaceVariant} />
        </TouchableOpacity>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={deposits}
        renderItem={renderItem}
        keyExtractor={item => item.id || ''}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={styles.empty}>
            <MaterialCommunityIcons name="bank-outline" size={48} color={theme.colors.onSurfaceVariant} />
            <Text style={[styles.emptyTitle, { color: theme.colors.onSurface }]}>No deposits yet</Text>
            <Text style={[styles.emptySubtitle, { color: theme.colors.onSurfaceVariant }]}>
              Tap + to record a deposit
            </Text>
          </View>
        }
        contentContainerStyle={deposits.length === 0 ? styles.emptyContainer : undefined}
      />
      <ConfirmDeleteDialog
        visible={depositToDelete !== null}
        onDismiss={() => setDepositToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Deposit"
        message="Are you sure you want to delete this deposit?"
        itemSummary={depositToDelete ? `${depositToDelete.description || 'Deposit'} · ${formatAmount(depositToDelete.amount)}` : undefined}
        loading={deleting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowAccent: {
    width: 3,
    alignSelf: 'stretch',
    borderRadius: 2,
    marginRight: 12,
  },
  rowContent: {
    flex: 1,
    paddingVertical: 14,
    paddingRight: 8,
  },
  rowTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
    marginBottom: 3,
  },
  rowSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
    marginRight: 8,
  },
  rowAmount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 15,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 9999,
  },
  statusText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
  },
  deleteBtn: {
    padding: 10,
    marginRight: 6,
  },
  empty: {
    alignItems: 'center',
    gap: 8,
  },
  emptyTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 17,
  },
  emptySubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
  },
});

export default DepositList;
